import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCompanyEnrichmentHistoryApi } from "../../../api/enrichmentApi";
import { AI_ENRICHMENT_TAB_INDEX } from "./companyEnrichmentFormat";

const HISTORY_LOAD_ERROR = "Unable to load enrichment history.";
const KNOWN_ACTION_TYPES = new Set(["find_email", "verify_email", "ai_enrichment"]);

function normalizedValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function resultIdentity(entry) {
  if (!entry || entry.status === "running") return null;
  const payload = entry.resultPayload || {};
  const actionType = entry.actionType || payload.actionType || "";
  const status = entry.status || payload.status || "";
  let result = "";

  if (status === "failed" && KNOWN_ACTION_TYPES.has(actionType)) {
    // Live and persisted failures word the same outcome differently ("Not found"
    // vs the backend summary), so a failure is identified by the action alone.
    result = "failed";
  } else if (actionType === "find_email") {
    result = normalizedValue(payload.email || payload.result || payload.summary);
  } else if (actionType === "verify_email") {
    result = [
      normalizedValue(payload.email),
      normalizedValue(payload.score ?? payload.result),
    ].join("|");
  } else if (actionType === "ai_enrichment") {
    result = normalizedValue(payload.summary || payload.result);
  } else if (status === "failed") {
    result = normalizedValue(payload.error || payload.summary);
  } else {
    result = normalizedValue(
      payload.detail || payload.summary || payload.email || payload.result
    );
  }

  if (!result) return null;
  return JSON.stringify([
    actionType,
    normalizedValue(entry.prompt),
    status,
    result,
  ]);
}

function mergeHistory(liveEntries, fetchedEntries) {
  const fetchedResultIds = new Set(
    fetchedEntries
      .map((entry) => normalizedValue(entry?.id))
      .filter(Boolean)
  );
  const fetchedIdentities = new Set(
    fetchedEntries.map(resultIdentity).filter(Boolean)
  );
  const unmatchedLiveEntries = liveEntries.filter((entry) => {
    const resultId = normalizedValue(entry?.resultPayload?.resultId);
    if (resultId && fetchedResultIds.has(resultId)) return false;
    const identity = resultIdentity(entry);
    return !identity || !fetchedIdentities.has(identity);
  });
  return [...unmatchedLiveEntries, ...fetchedEntries];
}

function mergeAdjacentHistoryPages(currentEntries, incomingEntries) {
  const entriesById = new Map();
  [...incomingEntries, ...currentEntries].forEach((entry) => {
    if (entry?.id && !entriesById.has(entry.id)) entriesById.set(entry.id, entry);
  });
  return Array.from(entriesById.values())
    .sort((left, right) => {
      const createdAtOrder = normalizedValue(right.createdAt).localeCompare(
        normalizedValue(left.createdAt)
      );
      return createdAtOrder || normalizedValue(right.id).localeCompare(
        normalizedValue(left.id)
      );
    })
    .slice(0, 100);
}

export default function useCompanyEnrichmentHistory({
  companyDetail,
  open,
  setActiveTab,
}) {
  const [liveHistoryByCompany, setLiveHistoryByCompany] = useState({});
  const [fetchedHistoryByCompany, setFetchedHistoryByCompany] = useState({});
  const [fetchStateByCompany, setFetchStateByCompany] = useState({});
  const [historyRefreshVersion, setHistoryRefreshVersion] = useState(0);
  const historyRequestInFlight = useRef(false);
  const historyRefreshPending = useRef(false);
  const preserveFetchedOnNextRequest = useRef(false);
  const companyKey =
    companyDetail?.stagedId ||
    companyDetail?.alphasearchId ||
    companyDetail?.primaryName ||
    "";
  const companyId = companyDetail?.stagedId || "";

  useEffect(() => {
    if (!open || !companyId || !companyKey) return undefined;
    let ignore = false;
    const preserveFetched = preserveFetchedOnNextRequest.current;
    preserveFetchedOnNextRequest.current = false;

    async function loadHistory() {
      historyRequestInFlight.current = true;
      try {
        const request = getCompanyEnrichmentHistoryApi(companyId);
        Promise.resolve().then(() => {
          if (ignore) return;
          setFetchStateByCompany((previous) => ({
            ...previous,
            [companyKey]: { loading: true, error: null },
          }));
        });
        const response = await request;
        if (ignore) return;
        const items = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        setFetchedHistoryByCompany((previous) => ({
          ...previous,
          [companyKey]: preserveFetched
            ? mergeAdjacentHistoryPages(previous[companyKey] || [], items)
            : items,
        }));
        setFetchStateByCompany((previous) => ({
          ...previous,
          [companyKey]: { loading: false, error: null },
        }));
      } catch (_error) {
        if (ignore) return;
        setFetchStateByCompany((previous) => ({
          ...previous,
          [companyKey]: { loading: false, error: HISTORY_LOAD_ERROR },
        }));
      } finally {
        if (!ignore) {
          historyRequestInFlight.current = false;
          if (historyRefreshPending.current) {
            historyRefreshPending.current = false;
            preserveFetchedOnNextRequest.current = true;
            setHistoryRefreshVersion((version) => version + 1);
          }
        }
      }
    }

    loadHistory();
    return () => {
      ignore = true;
      historyRequestInFlight.current = false;
      historyRefreshPending.current = false;
    };
  }, [companyId, companyKey, historyRefreshVersion, open]);

  const fetchState = fetchStateByCompany[companyKey] || {};

  const handleActionStart = useCallback(
    ({ id, actionType, prompt }) => {
      if (!companyKey) return;
      setActiveTab(AI_ENRICHMENT_TAB_INDEX);
      setLiveHistoryByCompany((previous) => ({
        ...previous,
        [companyKey]: [
          {
            id,
            prompt,
            actionType,
            status: "running",
            resultPayload: { actionType, status: "running" },
          },
          ...(previous[companyKey] || []),
        ],
      }));
    },
    [companyKey, setActiveTab]
  );

  const handleActionResult = useCallback(
    (statusPayload) => {
      if (!companyKey) return;
      const { entryId, ...payload } = statusPayload;
      setLiveHistoryByCompany((previous) => ({
        ...previous,
        [companyKey]: (previous[companyKey] || []).map((entry) =>
          entry.id === entryId
            ? {
              ...entry,
              status: payload.status || entry.status,
              resultPayload: { ...entry.resultPayload, ...payload },
            }
            : entry
        ),
      }));
      if (["success", "failed", "missing"].includes(payload.status)) {
        if (historyRequestInFlight.current) {
          historyRefreshPending.current = true;
        } else {
          setHistoryRefreshVersion((version) => version + 1);
        }
      }
    },
    [companyKey]
  );

  const enrichmentHistory = useMemo(
    () => mergeHistory(
      liveHistoryByCompany[companyKey] || [],
      fetchedHistoryByCompany[companyKey] || []
    ),
    [companyKey, fetchedHistoryByCompany, liveHistoryByCompany]
  );

  return {
    enrichmentHistory,
    enrichmentHistoryError: fetchState.error || null,
    enrichmentHistoryLoading: Boolean(fetchState.loading),
    handleActionResult,
    handleActionStart,
  };
}
